import {CompetitorService} from "./competitor.service";
import {LocalStorageService} from "./local-storage.service";
import {CompetitorTableRowData} from "@components";
import {Observable, of} from "rxjs";
import {Injectable} from "@angular/core";
import {FieldConfig, RaceAdditionalPricingItem} from "@model";

@Injectable({providedIn: 'root'})
export class CompetitorTableCacheService {

    private static readonly EVENT_TABLE_KEY = "competitor-table-event-cols-";

    private static readonly RACE_TABLE_KEY = "competitor-table-race-cols-";

    constructor(
        private competitorService: CompetitorService,
        private localStorageService: LocalStorageService,
    ) {}

    private createEventKey = (eventId: number, isMeasurement: boolean) =>
        `${CompetitorTableCacheService.EVENT_TABLE_KEY}${eventId}${isMeasurement ? '-meas' : ''}`;

    private createEventRaceKey = (eventId: number, raceId: number, isMeasurement: boolean) =>
        `${CompetitorTableCacheService.RACE_TABLE_KEY}${eventId}-${raceId}${isMeasurement ? '-meas' : ''}`;

    saveCompetitorTableColsForEvent(
        eventId: number,
        raceId: number | undefined,
        selectedCols: (keyof CompetitorTableRowData | string)[],
        selectedCustomCols?: FieldConfig[],
        selectedPricingItems?: RaceAdditionalPricingItem[],
        isMeasurement: boolean = false
    ) {
        const key = raceId
            ? this.createEventRaceKey(eventId, raceId, isMeasurement)
            : this.createEventKey(eventId, isMeasurement);
        if (raceId) {
            const payload = {
                cols: [...new Set(selectedCols)],
                customColIds: (selectedCustomCols ?? []).map(col => col.id),
                pricingItemIds: (selectedPricingItems ?? []).map(item => item.id),
            };
            this.localStorageService.saveData(key, JSON.stringify(payload));
        } else {
            const colSet = new Set(selectedCols);
            this.localStorageService.saveData(key, JSON.stringify([...colSet]));
        }
    }

    getCompetitorTableColsForEvent(
        eventId: number,
        raceId: number | undefined,
        isMeasurement: boolean
    ): Observable<{
        selectedCols: (keyof CompetitorTableRowData)[],
        selectedCustomColIds?: number[],
        selectedPricingItemIds?: number[]
    }> {
        const key = raceId
            ? this.createEventRaceKey(eventId, raceId, isMeasurement)
            : this.createEventKey(eventId, isMeasurement);

        const value = this.localStorageService.getData(key);
        if (!value) {
            return of({
                selectedCols: [],
                selectedCustomColIds: [],
                selectedPricingItemIds: [],
            });
        }

        try {
            const parsed = JSON.parse(value);

            if (raceId && typeof parsed === 'object' && parsed.cols) {
                return of({
                    selectedCols: parsed.cols,
                    selectedCustomColIds: parsed.customColIds ?? [],
                    selectedPricingItemIds: parsed.pricingItemIds ?? [],
                });
            } else {
                return of({
                    selectedCols: parsed,
                    selectedCustomColIds: [],
                    selectedPricingItemIds: [],
                });
            }
        } catch {
            return of({
                selectedCols: [],
                selectedCustomColIds: [],
                selectedPricingItemIds: [],
            });
        }
    }


    clearAllCompetitorTableCache(): void {
        const prefixes = [
            CompetitorTableCacheService.EVENT_TABLE_KEY,
            CompetitorTableCacheService.RACE_TABLE_KEY
        ];

        const allKeys = this.localStorageService.getAllKeys();

        for (const key of allKeys) {
            if (prefixes.some(prefix => key.startsWith(prefix))) {
                this.localStorageService.removeData(key);
            }
        }
    }

}
